"use client"

import React, { useState, useEffect } from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import LanguageSwitcher from "./LanguageSwitcher"
import DarkAndLightMode from "./DarkAndLightMode"
import { getStoredLanguage, setStoredLanguage } from "@/utils/languageDetection"
import Image from "next/image"

import blackLogo from "/public/images/logotype-dark-side.webp"
import whiteLogo from "/public/images/logotype-light-side.webp"

interface NavbarProps {
  lang: string
}

const Navbar: React.FC<NavbarProps> = ({ lang }) => {
  const [translations, setTranslations] = useState<any>(null)
  const [isActive, setActive] = useState<boolean>(false)
  const [menu, setMenu] = useState<boolean>(true)

  const pathname = usePathname()

  const isRouteActive = (route: string): boolean => {
    const currentPath = pathname
    if (route === "/") {
      return currentPath === "" || currentPath === "/"
    }
    return currentPath.startsWith(route)
  }

  const renderNavItem = (route: string, text: string) => {
    const isActive = isRouteActive(route)
    const activeStyle = {
      color: "var(--mainColor)",
      fontWeight: "600",
      cursor: "default",
      pointerEvents: "none" as const, // TypeScript requires this type assertion
    }

    return (
      <li className="nav-item">
        <Link
          href={route}
          style={isActive ? activeStyle : {}}
          className="nav-link"
          onClick={isActive ? (e) => e.preventDefault() : undefined}
          aria-current={isActive ? "page" : undefined}
        >
          {text}
        </Link>
      </li>
    )
  }

  const handleToggleSearchModal = () => {
    setActive(!isActive)
  }

  const toggleNavbar = () => {
    setMenu(!menu)
  }

  useEffect(() => {
    let elementId = document.getElementById("navbar")
    document.addEventListener("scroll", () => {
      if (window.scrollY > 100) {
        elementId?.classList.add("sticky")
      } else {
        elementId?.classList.remove("sticky")
      }
    })
  }, [])

  useEffect(() => {
    const fetchTranslations = async () => {
      setStoredLanguage(lang)
      try {
        const response = await fetch(`/api/translations/${lang}`)
        const data = await response.json()
        setTranslations(data)
      } catch (error) {
        console.error("Failed to fetch translations:", error)
      }
    }
    fetchTranslations()
  }, [lang])

  if (!translations) {
    return null
  }

  const classOne = menu
    ? "collapse navbar-collapse mean-menu"
    : "collapse navbar-collapse show"
  const classTwo = menu
    ? "navbar-toggler navbar-toggler-right collapsed"
    : "navbar-toggler navbar-toggler-right"

  return (
    <>
      <nav
        className="navbar navbar-expand-lg pt-0 pb-0 bg-color-f3f4f6"
        id="navbar"
      >
        <div className="container-fluid mw-1640">
          <Link className="navbar-brand" href="/">
            <Image
              src={blackLogo}
              width={243}
              className="main-logo"
              alt="Black logo"
              priority
            />
            <Image
              src={whiteLogo}
              width={243}
              className="white-logo d-none"
              alt="White logo"
              priority
            />
          </Link>

          <button
            onClick={toggleNavbar}
            className={classTwo}
            type="button"
            data-toggle="collapse"
            data-target="#navbarSupportedContent"
            aria-controls="navbarSupportedContent"
            aria-expanded="false"
            aria-label="Toggle navigation"
          >
            <span className="icon-bar top-bar"></span>
            <span className="icon-bar middle-bar"></span>
            <span className="icon-bar bottom-bar"></span>
          </button>

          <div className={classOne} id="navbarSupportedContent">
            <ul className="navbar-nav me-auto ms-70 mx-auto">
              {renderNavItem(
                "/",
                translations.common?.nav?.links?.home || "Inicio"
              )}
              {renderNavItem(
                "/contacto",
                translations.common?.nav?.links?.contact || "Contáctenos"
              )}
              {renderNavItem(
                "/nosotros",
                translations.common?.nav?.links?.about || "Sobre nosotros"
              )}
            </ul>
          </div>

          <div className="others-options">
            <ul className="d-flex align-items-center">
              <li>
                <LanguageSwitcher currentLang={lang} />
              </li>
              <li>
                <DarkAndLightMode />
              </li>
            </ul>
          </div>
        </div>
      </nav>

      <div
        className={`offcanvas offcanvas-top src-form-wrapper ${
          isActive ? "show" : ""
        }`}
      >
        <div className="container">
          <div className="offcanvas-body small">
            <form className="src-form">
              <input
                type="text"
                className="form-control"
                placeholder="Search Here..."
              />
              <button type="submit" className="src-btn">
                <i className="ri-search-line"></i>
              </button>
            </form>
          </div>
        </div>

        <button
          type="button"
          className="btn-close"
          onClick={handleToggleSearchModal}
        >
          <i className="ri-close-line"></i>
        </button>
      </div>
    </>
  )
}

export default Navbar
